<template>
<div class="row justify-content-md-center">

	<div class="col-12 col-xl-8">
		<!-- Title and action button section -->
		<div class="row">
			<div class="col-6">
				<h1 class="display-4">{{ $t('item.list') }}</h1>
			</div>

			<div class="col-6 d-flex align-items-center justify-content-end">
				<b-btn variant="outline-secondary"
					v-b-tooltip.hover
					title="Terug"
					exact
					:to="{ name: 'Landingpage' }">
					<i class="fa fa-fw fa-reply"></i>
				</b-btn>
			</div>
		</div>

		<b-alert v-if="error"
		         variant="danger"
		         show>{{ error }}</b-alert>

		<!-- Filter section -->
		<div class="my-1 row">
			<div class="col-12">
				<filter-options item person v-model="filterObj" @input="filterData" />
			</div>
		</div>

		<!-- Pagination section -->
		<div class="row">
			<div class="col-12 d-flex justify-content-end">
				<b-pagination-nav
					:link-gen="linkGen"
					:number-of-pages="items.last_page"
					:value="items.current_page"
					use-router
					class="pull-right"
				/>
			</div>
		</div>

		<partial-loader :loading="isFetching"></partial-loader>

		<div class="row">
			<div class="col-12">
				<b-table v-if="!isFetching"
						striped
						hover
						show-empty
						:items="items.data"
						:fields="fields"
						:per-page="parseInt(items.per_page)"
						:sort-by.sync="sortBy"
						:sort-desc.sync="sortDesc"
						:no-local-sorting="true"
						:empty-text="$t('item.listEmptyText')"
						:empty-filtered-text="$t('item.listEmptyFilteredText')"
						@sort-changed="sortingChanged"
				>
					<template v-slot:cell(media)="row">
						<b-img-lazy
							v-if="row.item.media[0]"
							:width="imgSize"
							:src="row.item.media[0].url.replace(row.item.media[0].filename, `small_${row.item.media[0].filename}`)" 
						/>
						<b-img v-else blank blank-color="#ccc" :width="imgSize" :height="imgSize" />
					</template>

					<template v-slot:cell(auction_order_number)="row">
							<span v-if="row.item.auction_order_number">{{ row.item.auction_order_number }}</span>
							<span v-else><i>-</i></span>
					</template>

					<template v-slot:cell(auction.abbreviation)="row">
							<span v-if="row.item.auction.abbreviation">{{ row.item.auction.abbreviation }}</span>
							<span v-else><i>Voeg afk. toe</i></span>
					</template>

					<template v-slot:cell(number)="row">
							{{ row.item.seller.code }}-{{ row.item.number }}
					</template>

					<template v-slot:cell(category)="row">
							<span v-if="row.item.category">{{ row.item.category.slug }}</span>
					</template>

					<template v-slot:cell(title)="row">
						<strong>{{ row.item.title }}</strong><br>
						<small>{{ row.item.description }}</small>
					</template>

					<template v-slot:cell(price)="row">
						<div v-if="row.item.bid">
							€{{ Math.round(row.item.bid.price) }},-
						</div>
						<div v-else>
							<i>-</i>
						</div>
					</template>

					<template v-slot:cell(actions)="row">
						<b-button-group class="pull-right">
							<b-btn variant="outline-primary" :to="{ name: 'item', params: { itemId: row.item.id, auctionId: row.item.auction_id } }"><i class="fa fa-fw fa-eye"></i></b-btn>
							<b-btn variant="outline-primary" :to="{ name: 'itemEdit', params: { itemId: row.item.id, auctionId: row.item.auction_id } }"><i class="fa fa-fw fa-edit"></i></b-btn>
						</b-button-group>
					</template>

					<template v-slot:cell(target_price_1)="row">
						<span v-if="row.item.target_price_1">{{ Math.round(row.item.target_price_1) }}</span>
						<span v-if="row.item.target_price_2">
							<span v-if="row.item.target_price_1">-</span>
							{{ Math.round(row.item.target_price_2) }}
						</span>
					</template>
				</b-table>
			</div>
		</div>

		<b-pagination-nav :link-gen="linkGen"
		                  :number-of-pages="items.last_page"
		                  :value="items.current_page"
		                  use-router
		                  class="pull-right" />
	</div>
</div>
</template>

<script>
import * as _ from 'lodash';
import PartialLoader from '@/components/elements/PartialLoader';
import { flashSuccess } from '@/helpers/flash-message-helper';
import ItemService from '@/services/item-service';
import RequestHelper from '@/helpers/request-helper';
import { isCancel, CancelToken } from 'axios';
import FilterOptions from '@/components/elements/Input/FilterOptions';

const requestHelper = new RequestHelper();

export default {
	components: {
		PartialLoader,
		FilterOptions
	},
	data() {
		return {
			items: [],
			isFetching: false,
			fields: [
				{ key: 'media', label: '' },
				{ key: 'auction_order_number', label: this.$t('item.auctionOrderNumberList'), sortable: true },
				{ key: 'auction.abbreviation', label: this.$t('common.auction'), sortable: true },
				{ key: 'number', label: this.$t('item.itemNumber'), sortable: true },
				{ key: 'category.abbreviation', label: this.$t('item.categoryShort').substring(0, 3), sortable: true },
				{ key: 'title', label: this.$t('item.title'), sortable: true },
				{ key: 'price', label: this.$t('item.pricePaid'), sortable: true },
				{ key: 'target_price_1', label: this.$t('item.targetpriceList'), sortable: true, },
				{ key: 'actions', label: this.$t('common.actions'), class: 'text-right' }
			],
			perPage: 50,
			sortBy: 'id',
			sortDesc: true,
			filter: '',
			imgSize: 80,
			error: false,
			cancelToken: null,
			filterObj: {
				item: {
                    tag: this.$route.query.tag,
                    category_id: this.$route.query.category_id,
                    title_description: this.$route.query.title_description,
					auction_order_number: this.$route.query.auction_order_number,
					auction_id: this.$route.query.auction_id,
				},

				person: {
					name: this.$route.query.name,
					code: this.$route.query.code,
					email: this.$route.query.email,
					address: this.$route.query.address,
					bid_number: this.$route.query.bid_number,
				}
			}
		}

	},
	watch: {
		'$route': 'fetchData',
		'sortBy': 'filterData',
		'sortDesc': 'filterData'
	},
	methods: {
		initialize() {
			this.fetchData();
		},
		linkGen(pageNumber) {
			let queryString = this.$route.query;
			queryString.page = pageNumber;

			const URL = Object.keys(queryString).map(key => key + '=' + queryString[key]).join('&');

			return '?' + URL;
		},
		filterData() {
			this.$router.push({ query: {
				..._.pickBy(this.filterObj.person, _.identity),
				..._.pickBy(this.filterObj.item, _.identity)
			}})
		},
		fetchData() {
			this.error = false;
			this.isFetching = true;
			const query = this.$route.query;

			if (!_.isNil(this.cancelToken)) {
				this.cancelToken.cancel();
			}

			this.cancelToken = CancelToken.source();

			const params = {
				total: this.perPage,
				sort: this.sortBy,
				order: this.sortDesc ? 'desc' : 'asc',
				...query
			};

			return ItemService.getItems(params, this.cancelToken.token).then(response => {
				this.items = response;
				this.isFetching = false;
			}).catch(error => {
				if (!isCancel(error)) {
					this.isFetching = false;
					this.error = error.message;
				}
			});
		},
		sortingChanged(ctx) {
			if (ctx.sortBy != null) {
				this.sortBy = ctx.sortBy;
				this.sortDesc = ctx.sortDesc;
			} else {
				this.resetSorting();
			}
		},
		resetSorting() {
			this.sortBy = 'id';
			this.sortDesc = true;
		},
	},
	created() {
		this.initialize();
	}
}
</script>



// WEBPACK FOOTER //
// ItemList.vue?69c72b1a